import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { AnimatePresence, motion } from "framer-motion";
import locationFox from "../asserts/Location.png";
import {
  IconArrowUpRight,
  IconBook2,
  IconBuilding,
  IconCheck,
  IconChevronDown,
  IconCode,
  IconMapPin,
  IconSend,
  IconThumbUp,
} from "@tabler/icons-react";

import Footer from "../components/Footer";

const WEB3FORMS_ACCESS_KEY =
  process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

/* =========================================================
   FAQ DATA
========================================================= */

const faqs = [
  {
    id: 1,

    question: "Where is WE3X located?",

    answer:
      "WE3X is based in Karaikudi, Tamil Nadu. Through WE3X Institute, we provide practical IT training for students and job seekers, while WE3X Technologies works with businesses on software development, websites, applications and digital marketing.",
  },

  {
    id: 2,

    question: "What services does WE3X Technologies provide?",

    answer:
      "WE3X Technologies provides custom software development, web applications, landing pages, mobile app development, billing and POS solutions, animated websites, SEO, Google Ads, social media marketing, lead generation and other digital solutions for businesses.",
  },

  {
    id: 3,

    question: "Does WE3X develop custom software for businesses?",

    answer:
      "Yes. WE3X Technologies develops custom software based on business requirements, including dashboards, web applications, CRM systems, billing software, inventory systems, POS solutions and business automation tools.",
  },

  {
    id: 4,

    question: "Does WE3X provide billing and POS software?",

    answer:
      "Yes. WE3X develops billing and POS solutions designed to simplify sales, inventory, product management, customer management and everyday shop operations.",
  },

  {
    id: 5,

    question: "Does WE3X provide digital marketing services in Karaikudi?",

    answer:
      "Yes. WE3X provides digital marketing services in Karaikudi including SEO, Google Ads, Meta Ads, social media marketing, content strategy, branding and lead-generation campaigns.",
  },

  {
    id: 6,

    question: "Does WE3X provide SEO services?",

    answer:
      "Yes. WE3X helps businesses improve their organic visibility through SEO strategies including website optimisation, keyword targeting, content planning, local SEO and technical improvements.",
  },

  {
    id: 7,

    question: "Does WE3X manage Google Ads campaigns?",

    answer:
      "Yes. WE3X provides Google Ads campaign planning, setup, optimisation and performance monitoring to help businesses reach relevant audiences and generate enquiries.",
  },

  {
    id: 8,

    question: "Which courses are available at WE3X Institute?",

    answer:
      "WE3X Institute provides practical training in areas including MERN Stack Development, Digital Marketing, UI/UX, Python Full Stack, Cyber Security, Tally Prime and Basic Computers.",
  },

  {
    id: 9,

    question:
      "Do beginners need previous coding experience to join WE3X Institute?",

    answer:
      "Not necessarily. Course requirements depend on the program. Beginner-friendly courses are structured to build fundamentals before progressing to practical projects and more advanced concepts.",
  },

  {
    id: 10,

    question:
      "Does WE3X provide internships, certification or placement assistance?",

    answer:
      "These opportunities depend on the selected course and current program structure. Contact WE3X Institute with the course you are interested in so the team can provide the latest details about internships, certification and placement support.",
  },
];

/* =========================================================
   MESSAGE SUGGESTIONS
========================================================= */

const messageSuggestions = {
  institute: {
    default: [
      "I would like to know more about the available courses.",
      "I would like to know about course fees, duration and class timings.",
      "I need details about internships, certification and placement support.",
    ],
    "MERN Stack": [
      "I would like to know the MERN Stack course fees and duration.",
      "I would like to know about the next MERN Stack batch and class timings.",
      "I need details about MERN Stack internship and placement support.",
    ],
    "Digital Marketing Course": [
      "I would like to know the Digital Marketing course fees and duration.",
      "I would like to know what topics are covered in the Digital Marketing course.",
      "I need details about practical training, certification and internship opportunities.",
    ],
    "UI/UX": [
      "I would like to know the UI/UX course fees and duration.",
      "I want to learn UI/UX design and would like to know the course syllabus.",
      "I would like to know whether practical projects are included in the UI/UX course.",
    ],
    "Python Full Stack": [
      "I would like to know the Python Full Stack course fees and duration.",
      "I would like to know about the next Python Full Stack batch.",
      "I need details about projects, internship and placement support.",
    ],
    "Cyber Security": [
      "I would like to know the Cyber Security course fees and duration.",
      "I am interested in Cyber Security and would like to know the syllabus.",
      "I would like to know about practical training and certification.",
    ],
    "Tally Prime": [
      "I would like to know the Tally Prime course fees and duration.",
      "I would like to know whether GST and practical accounting are covered.",
      "I would like to know the next available Tally Prime batch.",
    ],
    "Basic Computers": [
      "I would like to know the Basic Computer course fees and duration.",
      "I am a beginner and would like to know what topics are covered.",
      "I would like to know about class timings and the next batch.",
    ],
  },

  technology: {
    default: [
      "I would like to discuss my business requirement with the WE3X team.",
      "I would like to know the estimated cost and project timeline.",
      "I have an idea and would like your team to suggest the right solution.",
    ],
    "Web Application": [
      "I need a web application for my business and would like to discuss the requirements.",
      "I would like to know the estimated cost and timeline for developing a web application.",
      "I already have an idea for a web application and need technical guidance.",
    ],
    "Website Development": [
      "I need a professional website for my business and would like to discuss the requirements.",
      "I would like to know the estimated website development cost and timeline.",
      "I already have a website and would like to redesign and improve it.",
    ],
    "Landing Page": [
      "I need a landing page for my business or campaign.",
      "I would like to know the cost and timeline for creating a landing page.",
      "I need a high-converting landing page for lead generation.",
    ],
    "Mobile App": [
      "I have a mobile app idea and would like to discuss the requirements.",
      "I would like to know the estimated cost and timeline for mobile app development.",
      "I need a mobile application for my business.",
    ],
    "Billing / POS Software": [
      "I need billing and POS software for my business.",
      "I would like to know the features and pricing of your billing software.",
      "I need billing, inventory and customer management in one system.",
    ],
    "Animated Website": [
      "I need a premium animated website for my business.",
      "I would like to redesign my current website with modern animations.",
      "I would like to know the cost and timeline for an animated website.",
    ],
    SEO: [
      "I want to improve my website ranking on Google.",
      "I would like to know about your SEO packages and services.",
      "I need SEO support to generate more organic traffic and enquiries.",
    ],
    "Google Ads": [
      "I would like to run Google Ads for my business.",
      "I need help generating leads through Google Ads.",
      "I would like to know about Google Ads management pricing.",
    ],
    "Digital Marketing": [
      "I need digital marketing support for my business.",
      "I want to improve my online presence and generate more leads.",
      "I would like to know about your digital marketing packages.",
    ],
    "Video Editing": [
      "I need professional video editing for my business or social media content.",
      "I would like to know your video editing pricing.",
      "I have raw videos and need professional editing support.",
    ],
    Other: [
      "I have a custom requirement and would like to discuss it with your team.",
      "I need guidance in choosing the right technology solution for my business.",
      "I would like the WE3X team to contact me regarding my requirement.",
    ],
  },
};


/* =========================================================
   CONTACT PAGE
========================================================= */

export default function Contact() {
  const [contactType, setContactType] = useState("technology");

  const [openFaq, setOpenFaq] = useState(0);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitted, setSubmitted] = useState(false);

  const [selectedInterest, setSelectedInterest] = useState("");

  const [message, setMessage] = useState("");

  const currentSuggestions =
    messageSuggestions[contactType]?.[selectedInterest] ||
    messageSuggestions[contactType]?.default ||
    [];

  /* =========================================================
     SEO
  ========================================================= */

  useEffect(() => {
    document.title =
      "Contact WE3X | IT Training, Software & Digital Marketing in Karaikudi";

    let meta = document.querySelector('meta[name="description"]');

    if (!meta) {
      meta = document.createElement("meta");

      meta.name = "description";

      document.head.appendChild(meta);
    }

    meta.content =
      "Contact WE3X in Karaikudi, Tamil Nadu for IT training, software development, web and app development, billing software, SEO, Google Ads and digital marketing services.";

    /* FAQ SCHEMA */

    const existingSchema = document.getElementById("we3x-contact-faq-schema");

    if (existingSchema) {
      existingSchema.remove();
    }

    const schema = document.createElement("script");

    schema.id = "we3x-contact-faq-schema";

    schema.type = "application/ld+json";

    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",

      "@type": "FAQPage",

      mainEntity: faqs.map((faq) => ({
        "@type": "Question",

        name: faq.question,

        acceptedAnswer: {
          "@type": "Answer",

          text: faq.answer,
        },
      })),
    });

    document.head.appendChild(schema);

    return () => {
      const currentSchema = document.getElementById("we3x-contact-faq-schema");

      if (currentSchema) {
        currentSchema.remove();
      }
    };
  }, []);

  /* =========================================================
     SUBMIT
  ========================================================= */

  const handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);

    const name =
      formData.get("name")?.toString().trim() || "";

    const phone =
      formData.get("phone")?.toString().trim() || "";

    const email =
      formData.get("email")?.toString().trim() || "";

    const interest =
      formData.get("interest")?.toString().trim() || "";

    const enquiryMessage =
      formData.get("message")?.toString().trim() || "";

    if (!interest) {
      window.alert(
        "Please select what you're interested in."
      );
      return;
    }

    if (!WEB3FORMS_ACCESS_KEY) {
      console.error(
        "REACT_APP_WEB3FORMS_ACCESS_KEY is missing."
      );

      window.alert(
        "Email service is not configured yet. Please contact WE3X directly at connect@we3x.in."
      );

      return;
    }

    const enquiryType =
      contactType === "institute"
        ? "WE3X Institute"
        : "WE3X Technologies";

    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,

      subject:
        `New ${enquiryType} Enquiry - ${name}`,

      from_name: "WE3X Website",

      name,

      phone,

      email,

      contact_type: enquiryType,

      interest,

      message: enquiryMessage,
    };

    try {
      setIsSubmitting(true);

      const response = await fetch(
        "https://api.web3forms.com/submit",
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },

          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.message ||
            "Unable to send enquiry."
        );
      }

      form.reset();

      setSelectedInterest("");

      setMessage("");

      setSubmitted(true);
    } catch (error) {
      console.error(
        "Web3Forms submit failed:",
        error
      );

      window.alert(
        "Unable to send your enquiry right now. Please try again or email connect@we3x.in."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main
      className="
        overflow-hidden
        bg-[#f6f6f6]
        text-[#111111]
      "
    >
      {/* =====================================================
          HERO / CONTACT WORKSPACE
      ===================================================== */}

      <section
        className="
          px-[14px]
          pb-16
          pt-[115px]
          sm:px-5
          lg:pt-[125px]
        "
      >
        <div
          className="
            relative
            mx-auto
            max-w-[1320px]
            overflow-hidden
            rounded-[26px]
            bg-[#101010]
            text-white
          "
        >
          {/* GRID */}

          <div
            className="
              pointer-events-none
              absolute
              inset-0
              opacity-[0.04]
            "
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)",

              backgroundSize: "46px 46px",
            }}
          />

          {/* GLOW */}

          <div
            className="
              pointer-events-none
              absolute
              -left-[200px]
              -top-[200px]
              h-[600px]
              w-[600px]
              rounded-full
              bg-[#55aef3]/20
              blur-[150px]
            "
          />

          <div
            className="
              relative
              z-10
              grid
              min-h-[760px]
              lg:grid-cols-[0.9fr_1.1fr]
            "
          >
            {/* =================================================
                LEFT SIDE
            ================================================= */}

            <div
              className="
                flex
                flex-col
                justify-between
                border-b
                border-white/10
                px-6
                py-12
                sm:px-10
                lg:border-b-0
                lg:border-r
                lg:px-14
                lg:py-16
              "
            >
              <div>
                {/* LABEL */}

                <motion.div
                  initial={{
                    opacity: 0,
                    y: 15,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  className="
                    inline-flex
                    items-center
                    gap-2
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.22em]
                    text-[#55aef3]
                  "
                >
                  <span
                    className="
                      h-[1px]
                      w-8
                      bg-[#55aef3]
                    "
                  />
                  Start a conversation
                </motion.div>

                {/* HEADING */}

                <motion.h1
                  initial={{
                    opacity: 0,
                    y: 35,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.8,
                    delay: 0.1,
                  }}
                  className="
                    merriweather
                    mt-7
                    max-w-[580px]
                    text-[42px]
                    font-black
                    leading-[1.05]
                    tracking-[-0.045em]
                    sm:text-[54px]
                    lg:text-[62px]
                  "
                >
                  Tell us what
                  <span
                    className="
                      block
                      text-[#55aef3]
                    "
                  >
                    you're working on.
                  </span>
                </motion.h1>

                <motion.p
                  initial={{
                    opacity: 0,
                    y: 25,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    delay: 0.2,
                  }}
                  className="
                    mt-7
                    max-w-[540px]
                    text-[15px]
                    leading-8
                    text-white/50
                  "
                >
                  Looking for a course? Planning a new website? Need software
                  for your business? Want to improve your digital presence? Tell
                  us where you want to go. We'll help you find the right way
                  forward.
                </motion.p>

                {/* LOCATION */}

                <motion.div
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  transition={{
                    delay: 0.35,
                  }}
                  className="
                    mt-9
                    flex
                    items-center
                    gap-3
                    text-sm
                    text-white/60
                  "
                >
                  <div
                    className="
                      flex
                      h-10
                      w-10
                      items-center
                      justify-center
                      rounded-xl
                      border
                      border-white/10
                      bg-white/[0.04]
                      text-[#55aef3]
                    "
                  >
                    <IconMapPin size={18} />
                  </div>

                  <div>
                    <p
                      className="
                        text-[9px]
                        font-bold
                        uppercase
                        tracking-[0.18em]
                        text-white/25
                      "
                    >
                      Based in
                    </p>

                    <p
                      className="
                        mt-1
                        font-semibold
                        text-white/70
                      "
                    >
                      Karaikudi, Tamil Nadu
                    </p>
                  </div>
                </motion.div>
              </div>


              {/* =================================================
                  INTERACTIVE PARTICLE TEXT
              ================================================= */}

              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  duration: 0.8,
                  delay: 0.45,
                }}
                className="
                  relative
                  mt-8
                  min-h-[240px]
                  flex-1
                  overflow-hidden
                  lg:min-h-[280px]
                "
              >
                <div
                  className="
                    pointer-events-none
                    absolute
                    left-0
                    top-4
                    z-10
                    flex
                    items-center
                    gap-3
                  "
                >
                  <span className="h-[1px] w-6 bg-white/15" />

                  <p
                    className="
                      text-[8px]
                      font-bold
                      uppercase
                      tracking-[0.2em]
                      text-white/20
                    "
                  >
                    Move your cursor
                  </p>
                </div>

                <ParticleText
                  text="WE3X"
                  particleSize={2}
                  density={4}
                  color="#ffffff"
                  highlightColor="#55aef3"
                  scatter={165}
                  gatherDuration={1500}
                  stagger={380}
                  pointerRepel={42}
                  repelRadius={120}
                  idleDrift={0.65}
                  trigger="hover"
                  fontSize="clamp(5rem, 10vw, 9rem)"
                  fontWeight={900}
                  fontFamily="inherit"
                  glow
                  className="h-full"
                />

                <div
                  className="
                    pointer-events-none
                    absolute
                    bottom-0
                    left-1/2
                    h-24
                    w-3/4
                    -translate-x-1/2
                    rounded-full
                    bg-[#55aef3]/5
                    blur-[70px]
                  "
                />
              </motion.div>

              {/* CONVERSATION LINE */}

              <div
                className="
                  mt-3
                  border-t
                  border-white/10
                  pt-7
                "
              >
                <p
                  className="
                    max-w-[430px]
                    text-xs
                    leading-6
                    text-white/30
                  "
                >
                  WE3X brings practical IT education, software development and
                  digital marketing together under one technology-driven brand.
                </p>
              </div>
            </div>

            {/* =================================================
                RIGHT / FORM
            ================================================= */}

            <div
              className="
                relative
                bg-white
                p-5
                text-black
                sm:p-8
                lg:p-12
              "
            >
              <AnimatePresence mode="wait">
                {!submitted ? (
                  <motion.div
                    key="form"
                    initial={{
                      opacity: 0,
                    }}
                    animate={{
                      opacity: 1,
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.95,
                    }}
                  >
                    {/* ROUTE SELECTOR */}

                    <div>
                      <p
                        className="
                          text-[10px]
                          font-bold
                          uppercase
                          tracking-[0.18em]
                          text-neutral-400
                        "
                      >
                        What brings you here?
                      </p>

                      <div
                        className="
                          mt-4
                          grid
                          gap-3
                          sm:grid-cols-2
                        "
                      >
                        <ContactPath
                          active={contactType === "institute"}
                          icon={<IconBook2 size={20} />}
                          title="I want to learn"
                          subtitle="WE3X Institute"
                          onClick={() => {
                            setContactType("institute");
                            setSelectedInterest("");
                            setMessage("");
                          }}
                        />

                        <ContactPath
                          active={contactType === "technology"}
                          icon={<IconCode size={20} />}
                          title="I want to build"
                          subtitle="WE3X Technologies"
                          onClick={() => {
                            setContactType("technology");
                            setSelectedInterest("");
                            setMessage("");
                          }}
                        />
                      </div>
                    </div>

                    {/* FORM */}

                    <form
                      onSubmit={handleSubmit}
                      className="
                        mt-9
                      "
                    >
                      <div
                        className="
                          grid
                          gap-6
                          sm:grid-cols-2
                        "
                      >
                        <ContactField
                          label="Your name"
                          name="name"
                          placeholder="Enter your name"
                          required
                        />

                        <ContactField
                          label="Phone number"
                          name="phone"
                          type="tel"
                          placeholder="Enter phone number"
                          required
                        />
                      </div>

                      <div
                        className="
                          mt-6
                          grid
                          gap-6
                          sm:grid-cols-2
                        "
                      >
                        <ContactField
                          label="Email address"
                          name="email"
                          type="email"
                          placeholder="you@example.com"
                        />

                        <InterestDropdown
                          key={contactType}
                          contactType={contactType}
                          onSelect={(value) => {
                            setSelectedInterest(value);
                            setMessage("");
                          }}
                        />
                      </div>

                      {/* MESSAGE */}

                      <div className="mt-7">
                        <label
                          className="
                            text-[10px]
                            font-bold
                            uppercase
                            tracking-[0.15em]
                            text-neutral-400
                          "
                        >
                          Tell us a little more
                        </label>

                        {/* AUTO SUGGESTIONS */}

                        <div className="mt-4">
                          <p
                            className="
                              mb-3
                              text-[9px]
                              font-bold
                              uppercase
                              tracking-[0.14em]
                              text-neutral-300
                            "
                          >
                            Quick suggestions
                          </p>

                          <div className="flex flex-wrap gap-2">
                            {currentSuggestions.map((suggestion, index) => (
                              <motion.button
                                key={suggestion}
                                type="button"
                                initial={{
                                  opacity: 0,
                                  y: 8,
                                }}
                                animate={{
                                  opacity: 1,
                                  y: 0,
                                }}
                                transition={{
                                  delay: index * 0.05,
                                }}
                                onClick={() => setMessage(suggestion)}
                                className={`
                                  rounded-full
                                  border
                                  px-3
                                  py-2
                                  text-left
                                  text-[10px]
                                  font-semibold
                                  leading-5
                                  transition-all
                                  duration-300

                                  ${
                                    message === suggestion
                                      ? "border-[#55aef3] bg-blue-50 text-[#3899e4]"
                                      : "border-neutral-200 bg-[#f8f8f8] text-neutral-500 hover:border-[#55aef3] hover:bg-blue-50 hover:text-[#3899e4]"
                                  }
                                `}
                              >
                                {suggestion}
                              </motion.button>
                            ))}
                          </div>
                        </div>

                        {/* TEXTAREA */}

                        <textarea
                          name="message"
                          rows="4"
                          value={message}
                          onChange={(event) => setMessage(event.target.value)}
                          placeholder={
                            contactType === "institute"
                              ? "Which course are you interested in? Tell us your current background or what you'd like to learn."
                              : "Tell us what you're planning to build, improve or grow."
                          }
                          required
                          className="
                            mt-5
                            w-full
                            resize-none
                            rounded-[16px]
                            border
                            border-neutral-200
                            bg-[#f8f8f8]
                            p-4
                            text-sm
                            leading-7
                            outline-none
                            transition
                            placeholder:text-neutral-400
                            focus:border-[#55aef3]
                            focus:bg-white
                          "
                        />

                        <div className="mt-2 flex justify-end">
                          <span
                            className="
                              text-[9px]
                              font-medium
                              text-neutral-300
                            "
                          >
                            {message.length} characters
                          </span>
                        </div>
                      </div>

                      {/* SUBMIT */}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="
                          group
                          mt-7
                          flex
                          w-full
                          items-center
                          justify-between
                          rounded-[14px]
                          bg-[#111]
                          px-5
                          py-4
                          text-sm
                          font-bold
                          text-white
                          transition-all
                          duration-300
                          hover:bg-[#55aef3]
                          disabled:cursor-wait
                        "
                      >
                        <span>
                          {isSubmitting ? "Sending..." : "Send your enquiry"}
                        </span>

                        <motion.span
                          animate={
                            isSubmitting
                              ? {
                                  x: [0, 7, 0],
                                }
                              : {}
                          }
                          transition={{
                            repeat: Infinity,
                            duration: 0.8,
                          }}
                        >
                          <IconSend size={18} />
                        </motion.span>
                      </button>
                    </form>
                  </motion.div>
                ) : (
                  /* =================================================
                     SUCCESS ANIMATION
                  ================================================= */

                  <SuccessMessage onReset={() => setSubmitted(false)} />
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          TWO JOURNEYS
      ===================================================== */}

      <section
        className="
          bg-[#f6f6f6]
          py-20
          sm:py-24
        "
      >
        <div
          className="
            mx-auto
            max-w-7xl
            px-5
            sm:px-8
            lg:px-10
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
          >
            <p
              className="
                text-[10px]
                font-bold
                uppercase
                tracking-[0.2em]
                text-[#3899e4]
              "
            >
              Find your direction
            </p>

            <h2
              className="
                merriweather
                mt-4
                max-w-3xl
                text-3xl
                font-black
                tracking-[-0.04em]
                sm:text-4xl
                lg:text-[52px]
                flex
                flex-col
                gap-3
              "
            >
              <span>
                One brand.
              </span>
              <span
                className="
                  block
                  text-neutral-400
                "
              >
                Two different journeys.
              </span>
            </h2>
          </motion.div>

          <div
            className="
              mt-12
              border-y
              border-neutral-200
            "
          >
            <JourneyRow
              number="01"
              icon={<IconBook2 />}
              title="Build your skills."
              name="WE3X Institute"
              description="Practical, career-focused IT training for students, graduates, beginners and job seekers."
              link="/institute"
              linkText="Explore IT Training in Karaikudi"
            />

            <JourneyRow
              number="02"
              icon={<IconBuilding />}
              title="Build your business."
              name="WE3X Technologies"
              description="Software development, websites, mobile apps, billing systems and digital marketing solutions for businesses."
              link="/technology"
              linkText="Explore WE3X Technology Services"
              last
            />
          </div>
        </div>
      </section>

      {/* =====================================================
          LOCAL SEO SECTION
      ===================================================== */}

      {/* =====================================================
    WE3X LOCATION — KARAikudi
===================================================== */}

      <section
        className="
    relative
    overflow-hidden
    bg-white
    py-20
    sm:py-24
    lg:py-28
  "
      >
        {/* BACKGROUND DECORATION */}

        <div
          className="
      pointer-events-none
      absolute
      -left-[250px]
      top-1/2
      h-[550px]
      w-[550px]
      -translate-y-1/2
      rounded-full
      bg-[#55aef3]/10
      blur-[150px]
    "
        />

        <div
          className="
      mx-auto
      max-w-7xl
      px-5
      sm:px-8
      lg:px-10
    "
        >
          <div
            className="
        grid
        items-center
        gap-14
        lg:grid-cols-[1.05fr_0.95fr]
        lg:gap-20
      "
          >
            {/* =====================================================
          CLICKABLE LOCATION EXPERIENCE
      ===================================================== */}

            <motion.a
              href="https://maps.app.goo.gl/ko38t3Zg5XTPRodw5"
              target="_blank"
              rel="noreferrer"
              initial={{
                opacity: 0,
                x: -40,
                scale: 0.96,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
                scale: 1,
              }}
              viewport={{
                once: true,
                amount: 0.25,
              }}
              transition={{
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
              whileHover={{
                y: -6,
              }}
              className="
          group
          relative
          block
          min-h-[520px]
          overflow-hidden
          rounded-[28px]
          border
          border-neutral-200
          bg-[#f5f9fc]
          shadow-xl
          shadow-black/[0.04]
          sm:min-h-[570px]
        "
              aria-label="Open WE3X location in Google Maps"
            >
              {/* =================================================
            MAP STYLE BACKGROUND
        ================================================= */}

              <div
                className="
            pointer-events-none
            absolute
            inset-0
            opacity-[0.5]
          "
                style={{
                  backgroundImage: `
              linear-gradient(
                rgba(85,174,243,0.09) 1px,
                transparent 1px
              ),
              linear-gradient(
                90deg,
                rgba(85,174,243,0.09) 1px,
                transparent 1px
              )
            `,
                  backgroundSize: "44px 44px",
                }}
              />

              {/* LARGE SOFT GLOW */}

              <div
                className="
            pointer-events-none
            absolute
            -left-[120px]
            top-[40px]
            h-[380px]
            w-[380px]
            rounded-full
            bg-[#55aef3]/20
            blur-[110px]
          "
              />

              {/* MAP ROAD LINES */}

              <motion.div
                animate={{
                  x: [0, 8, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="
            pointer-events-none
            absolute
            left-[-8%]
            top-[29%]
            h-[3px]
            w-[78%]
            rotate-[8deg]
            rounded-full
            bg-[#55aef3]/15
          "
              />

              <motion.div
                animate={{
                  x: [0, -8, 0],
                }}
                transition={{
                  duration: 7,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="
            pointer-events-none
            absolute
            left-[4%]
            top-[53%]
            h-[3px]
            w-[72%]
            -rotate-[11deg]
            rounded-full
            bg-neutral-300/40
          "
              />

              <div
                className="
            pointer-events-none
            absolute
            left-[22%]
            top-[-8%]
            h-[75%]
            w-[3px]
            rotate-[18deg]
            rounded-full
            bg-neutral-300/30
          "
              />

              {/* =================================================
            TOP LABEL
        ================================================= */}

              <div
                className="
            absolute
            left-5
            top-5
            z-20
            sm:left-7
            sm:top-7
          "
              >
                <div
                  className="
              inline-flex
              items-center
              gap-2
              rounded-full
              border
              border-white/70
              bg-white/80
              px-4
              py-2
              text-[10px]
              font-bold
              uppercase
              tracking-[0.18em]
              text-neutral-600
              shadow-sm
              backdrop-blur-xl
            "
                >
                  <span
                    className="
                relative
                flex
                h-2
                w-2
              "
                  >
                    <span
                      className="
                  absolute
                  inline-flex
                  h-full
                  w-full
                  animate-ping
                  rounded-full
                  bg-[#55aef3]
                  opacity-50
                "
                    />

                    <span
                      className="
                  relative
                  inline-flex
                  h-2
                  w-2
                  rounded-full
                  bg-[#55aef3]
                "
                    />
                  </span>
                  WE3X Location
                </div>
              </div>

              {/* =================================================
            LOCATION PIN
        ================================================= */}

              <div
                className="
            absolute
            left-[9%]
            top-[29%]
            z-20
            sm:left-[12%]
          "
              >
                {/* PULSES */}

                <motion.div
                  animate={{
                    scale: [1, 1.8, 1],
                    opacity: [0.4, 0, 0.4],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeOut",
                  }}
                  className="
              absolute
              inset-0
              rounded-full
              bg-[#55aef3]/30
            "
                />

                <div
                  className="
              relative
              flex
              h-16
              w-16
              items-center
              justify-center
              rounded-[20px]
              bg-[#55aef3]
              text-white
              shadow-2xl
              shadow-blue-200
            "
                >
                  <IconMapPin size={28} stroke={1.8} />
                </div>
              </div>

              {/* =================================================
            LOCATION CONTENT
        ================================================= */}

              <div
                className="
            absolute
            bottom-8
            left-6
            z-30
            max-w-[270px]
            sm:bottom-10
            sm:left-9
            sm:max-w-[320px]
          "
              >
                <p
                  className="
              text-[10px]
              font-bold
              uppercase
              tracking-[0.2em]
              text-[#3899e4]
            "
                >
                  Visit WE3X
                </p>

                <h3
                  className="
              merriweather
              mt-2
              text-3xl
              font-black
              tracking-[-0.04em]
              text-neutral-950
              sm:text-4xl
            "
                >
                  Karaikudi
                  <span
                    className="
                block
                text-[#55aef3]
              "
                  >
                    Tamil Nadu.
                  </span>
                </h3>

                <p
                  className="
              mt-4
              max-w-[280px]
              text-xs
              leading-6
              text-neutral-500
            "
                >
                  Want to visit us in person? Tap anywhere on this card and
                  Google Maps will take you there.
                </p>

                {/* CLICK CTA */}

                <div
                  className="
              mt-5
              inline-flex
              items-center
              gap-3
              rounded-full
              bg-[#111]
              px-5
              py-3
              text-xs
              font-bold
              text-white
              transition-all
              duration-300
              group-hover:bg-[#55aef3]
            "
                >
                  Find us on Google Maps
                  <IconArrowUpRight
                    size={15}
                    className="
                transition-transform
                duration-300
                group-hover:translate-x-1
                group-hover:-translate-y-1
              "
                  />
                </div>
              </div>

              {/* =================================================
            FOX IMAGE — RIGHT SIDE
        ================================================= */}

              <motion.img
                src={locationFox}
                alt="WE3X mascot inviting visitors to locate WE3X in Karaikudi"
                initial={{
                  opacity: 0,
                  x: 50,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.9,
                  delay: 0.2,
                  ease: [0.22, 1, 0.36, 1],
                }}
                animate={{
                  y: [0, -8, 0],
                }}
                className="
            pointer-events-none
            absolute
            bottom-0
            right-[-65px]
            z-20
            h-[78%]
            w-auto
            object-contain
            object-bottom
            transition-transform
            duration-500
            group-hover:scale-[1.03]
            sm:right-[-35px]
            sm:h-[88%]
            lg:right-[-55px]
          "
              />
              {/* =================================================
                BOTTOM BLUE LINE
              ================================================= */}

              <div
                className="
            absolute
            bottom-0
            left-0
            h-[3px]
            w-0
            bg-[#55aef3]
            transition-all
            duration-700
            group-hover:w-full
          "
              />
            </motion.a>

            {/* =====================================================
          LOCATION SEO CONTENT
      ===================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                x: 40,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{
                once: true,
                amount: 0.25,
              }}
              transition={{
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <div
                className="
            inline-flex
            items-center
            gap-3
          "
              >
                <span
                  className="
              h-[1px]
              w-8
              bg-[#55aef3]
            "
                />

                <p
                  className="
              text-[10px]
              font-bold
              uppercase
              tracking-[0.2em]
              text-[#3899e4]
            "
                >
                  WE3X in Karaikudi
                </p>
              </div>

              <h2
                className="
            merriweather
            mt-5
            text-3xl
            font-black
            tracking-[-0.04em]
            sm:text-4xl
            lg:text-[52px]
            lg:leading-[1.08]
          "
              >
                Technology, learning and growth
                <span
                  className="
              block
              text-[#55aef3]
            "
                >
                  closer to home.
                </span>
              </h2>

              <p
                className="
            mt-7
            max-w-2xl
            text-[15px]
            leading-8
            text-neutral-600
          "
              >
                Located in Karaikudi, Tamil Nadu, WE3X brings together
                professional IT education, software development and digital
                marketing under one technology-driven brand.
              </p>

              <p
                className="
            mt-4
            max-w-2xl
            text-[15px]
            leading-8
            text-neutral-600
          "
              >
                WE3X Institute focuses on practical, industry-relevant learning,
                while WE3X Technologies helps businesses create digital
                products, improve online visibility and simplify operations
                through technology.
              </p>

              {/* =================================================
            SMALL LOCATION CTA
        ================================================= */}

              <a
                href="https://maps.app.goo.gl/ko38t3Zg5XTPRodw5"
                target="_blank"
                rel="noreferrer"
                className="
            group
            mt-7
            inline-flex
            items-center
            gap-3
            text-sm
            font-black
            text-neutral-950
          "
              >
                <span
                  className="
              flex
              h-9
              w-9
              items-center
              justify-center
              rounded-full
              bg-blue-50
              text-[#55aef3]
              transition
              group-hover:bg-[#55aef3]
              group-hover:text-white
            "
                >
                  <IconMapPin size={17} />
                </span>
                Get directions to WE3X
                <IconArrowUpRight
                  size={16}
                  className="
              text-neutral-400
              transition-transform
              group-hover:translate-x-1
              group-hover:-translate-y-1
              group-hover:text-[#55aef3]
            "
                />
              </a>

              {/* =================================================
            SERVICES
        ================================================= */}

              <div
                className="
            mt-9
            border-t
            border-neutral-200
            pt-7
          "
              >
                <p
                  className="
              mb-4
              text-[9px]
              font-bold
              uppercase
              tracking-[0.18em]
              text-neutral-400
            "
                >
                  What you'll find at WE3X
                </p>

                <div
                  className="
              flex
              flex-wrap
              gap-3
            "
                >
                  <LocationTag text="IT Training" />

                  <LocationTag text="Software Development" />

                  <LocationTag text="Web & App Development" />

                  <LocationTag text="Digital Marketing" />

                  <LocationTag text="Billing & POS" />

                  <LocationTag text="SEO & Google Ads" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* =====================================================
          FAQ
      ===================================================== */}

      <section
        className="
          bg-[#101010]
          py-20
          text-white
          sm:py-24
          lg:py-28
        "
      >
        <div
          className="
            mx-auto
            max-w-7xl
            px-5
            sm:px-8
            lg:px-10
          "
        >
          <div
            className="
              grid
              gap-12
              lg:grid-cols-[0.65fr_1.35fr]
              lg:gap-20
            "
          >
            {/* FAQ INTRO */}

            <div>
              <div
                className="
                  lg:sticky
                  lg:top-[140px]
                "
              >
                <p
                  className="
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.2em]
                    text-[#55aef3]
                  "
                >
                  Questions & Answers
                </p>

                <h2
                  className="
                    merriweather
                    mt-5
                    text-3xl
                    font-black
                    tracking-[-0.04em]
                    sm:text-4xl
                    lg:text-[48px]
                    lg:leading-[1.1]
                  "
                >
                  Before you ask,
                  <span
                    className="
                      block
                      text-white/30
                    "
                  >
                    we may already have the answer.
                  </span>
                </h2>

                <p
                  className="
                    mt-6
                    max-w-md
                    text-sm
                    leading-7
                    text-white/40
                  "
                >
                  Find quick answers about WE3X Institute, software development,
                  billing systems and digital marketing services.
                </p>
              </div>
            </div>

            {/* FAQ ACCORDION */}

            <div
              className="
                border-t
                border-white/10
              "
            >
              {faqs.map((faq, index) => (
                <FaqItem
                  key={faq.id}
                  faq={faq}
                  index={index}
                  open={openFaq === index}
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}


/* =========================================================
   PARTICLE TEXT
   Integrated from the provided React Bits JavaScript variant.
   Styles are expressed with the project's existing Tailwind setup,
   so no separate ParticleText.css file is required.
========================================================= */

const hexToRgb = (hex) => {
  const clean = hex.replace("#", "").trim();

  if (!/^[0-9a-fA-F]{6}$/.test(clean)) {
    return null;
  }

  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
};

const mixRgb = (from, to, amount) => ({
  r: Math.round(from.r + (to.r - from.r) * amount),
  g: Math.round(from.g + (to.g - from.g) * amount),
  b: Math.round(from.b + (to.b - from.b) * amount),
});

const rgbToCss = (rgb) => `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const resolveFontSize = (
  value,
  container,
  fontWeight,
  fontFamily
) => {
  if (typeof value === "number") {
    return value;
  }

  const probe = document.createElement("span");

  probe.textContent = "M";
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.fontSize = value;
  probe.style.fontWeight = String(fontWeight);
  probe.style.fontFamily = fontFamily;

  container.appendChild(probe);

  const size =
    parseFloat(window.getComputedStyle(probe).fontSize) || 96;

  probe.remove();

  return size;
};

const waitForFonts = async (font) => {
  if (!("fonts" in document)) {
    return;
  }

  try {
    await document.fonts.load(font);
  } catch {
    // Continue with the available font if a custom font cannot be loaded.
  }

  await document.fonts.ready;
};

function ParticleText({
  text = "React Bits",
  particleSize = 2,
  density = 4,
  color = "#ffffff",
  highlightColor = "#8b5cf6",
  scatter = 180,
  gatherDuration = 1600,
  stagger = 420,
  pointerRepel = 40,
  repelRadius = 120,
  idleDrift = 0.7,
  trigger = "mount",
  fontSize = "clamp(3rem, 12vw, 8rem)",
  fontWeight = 800,
  fontFamily = "inherit",
  glow = true,
  className = "",
  style,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const container = containerRef.current;
    const canvas = canvasRef.current;

    if (!container || !canvas) {
      return undefined;
    }

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return undefined;
    }

    let particles = [];
    let animationFrame = null;
    let resizeFrame = null;
    let buildId = 0;
    let gathering = false;
    let gatherStart = 0;
    let reducedMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")
        .matches ?? false;
    let width = 0;
    let height = 0;
    let dpr = 1;

    const pointer = {
      active: false,
      x: 0,
      y: 0,
      smoothX: 0,
      smoothY: 0,
    };

    const startGather = (fromScatter = true) => {
      if (!particles.length) {
        return;
      }

      const now = performance.now();
      const spread = reducedMotion ? 0 : scatter;

      particles.forEach((particle) => {
        if (fromScatter) {
          const angle = particle.seed * Math.PI * 2;
          const distance =
            spread * (0.35 + particle.depth * 0.75);

          particle.x =
            particle.targetX +
            Math.cos(angle) * distance +
            (particle.depth - 0.5) * spread * 0.55;

          particle.y =
            particle.targetY +
            Math.sin(angle) * distance +
            (particle.seed - 0.5) * spread * 0.55;
        }

        particle.startX = particle.x;
        particle.startY = particle.y;
        particle.delay = reducedMotion
          ? 0
          : particle.seed * stagger;
      });

      gatherStart = now;
      gathering = true;
    };

    const drawParticle = (particle) => {
      const size = particle.size;

      ctx.fillStyle = particle.color;

      if (size <= 2.1) {
        ctx.fillRect(
          particle.x - size / 2,
          particle.y - size / 2,
          size,
          size
        );

        return;
      }

      ctx.beginPath();
      ctx.arc(
        particle.x,
        particle.y,
        size / 2,
        0,
        Math.PI * 2
      );
      ctx.fill();
    };

    const render = (now) => {
      ctx.clearRect(0, 0, width, height);

      if (glow && !reducedMotion) {
        ctx.shadowBlur = particleSize * 3;
        ctx.shadowColor = highlightColor;
      } else {
        ctx.shadowBlur = 0;
      }

      pointer.smoothX +=
        (pointer.x - pointer.smoothX) * 0.18;

      pointer.smoothY +=
        (pointer.y - pointer.smoothY) * 0.18;

      let complete = true;

      particles.forEach((particle) => {
        let baseX = particle.targetX;
        let baseY = particle.targetY;
        let progress = 1;

        if (gathering) {
          const local =
            (now - gatherStart - particle.delay) /
            Math.max(
              1,
              reducedMotion ? 1 : gatherDuration
            );

          progress = clamp(local, 0, 1);

          const eased = easeOutCubic(progress);

          baseX =
            particle.startX +
            (particle.targetX - particle.startX) * eased;

          baseY =
            particle.startY +
            (particle.targetY - particle.startY) * eased;

          if (progress < 1) {
            complete = false;
          }
        } else if (
          !reducedMotion &&
          idleDrift > 0
        ) {
          const driftTime = now * 0.001;

          baseX +=
            Math.sin(
              driftTime * 0.9 +
                particle.seed * 10
            ) *
            idleDrift *
            particle.depth;

          baseY +=
            Math.cos(
              driftTime * 0.75 +
                particle.depth * 10
            ) *
            idleDrift *
            particle.depth;
        }

        if (
          pointer.active &&
          !reducedMotion &&
          pointerRepel > 0 &&
          repelRadius > 0
        ) {
          const dx = baseX - pointer.smoothX;
          const dy = baseY - pointer.smoothY;
          const distance = Math.hypot(dx, dy);

          if (
            distance > 0 &&
            distance < repelRadius
          ) {
            const force =
              Math.pow(
                1 - distance / repelRadius,
                2
              ) * pointerRepel;

            baseX +=
              (dx / distance) * force;

            baseY +=
              (dy / distance) * force;
          }
        }

        const follow = reducedMotion ? 1 : 0.22;

        particle.x +=
          (baseX - particle.x) * follow;

        particle.y +=
          (baseY - particle.y) * follow;

        ctx.globalAlpha = clamp(
          0.35 + progress * 0.65,
          0,
          1
        );

        drawParticle(particle);
      });

      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;

      if (gathering && complete) {
        gathering = false;
      }

      animationFrame =
        window.requestAnimationFrame(render);
    };

    const ensureRenderLoop = () => {
      if (animationFrame === null) {
        animationFrame =
          window.requestAnimationFrame(render);
      }
    };

    const sampleText = async () => {
      const currentBuild = ++buildId;
      const rect =
        container.getBoundingClientRect();

      width = Math.floor(rect.width);
      height = Math.floor(rect.height);

      if (width <= 0 || height <= 0) {
        return;
      }

      dpr = Math.min(
        window.devicePixelRatio || 1,
        2
      );

      canvas.width = Math.max(
        1,
        Math.floor(width * dpr)
      );

      canvas.height = Math.max(
        1,
        Math.floor(height * dpr)
      );

      canvas.style.width = "100%";
      canvas.style.height = "100%";

      ctx.setTransform(
        dpr,
        0,
        0,
        dpr,
        0,
        0
      );

      const computed =
        window.getComputedStyle(container);

      const resolvedFamily =
        fontFamily === "inherit"
          ? computed.fontFamily || "sans-serif"
          : fontFamily;

      let resolvedSize = resolveFontSize(
        fontSize,
        container,
        fontWeight,
        resolvedFamily
      );

      let font = `${fontWeight} ${resolvedSize}px ${resolvedFamily}`;

      await waitForFonts(font);

      if (currentBuild !== buildId) {
        return;
      }

      const offscreen =
        document.createElement("canvas");

      const offCtx = offscreen.getContext(
        "2d",
        {
          willReadFrequently: true,
        }
      );

      if (!offCtx) {
        return;
      }

      const content = String(text || " ");
      const maxTextWidth = width * 0.92;

      offCtx.font = font;

      let metrics =
        offCtx.measureText(content);

      const measuredWidth = Math.max(
        1,
        metrics.width
      );

      if (measuredWidth > maxTextWidth) {
        resolvedSize = Math.max(
          18,
          resolvedSize *
            (maxTextWidth / measuredWidth)
        );

        font = `${fontWeight} ${resolvedSize}px ${resolvedFamily}`;

        await waitForFonts(font);

        if (currentBuild !== buildId) {
          return;
        }

        offCtx.font = font;
        metrics =
          offCtx.measureText(content);
      }

      const left = Math.ceil(
        metrics.actualBoundingBoxLeft || 0
      );

      const right = Math.ceil(
        metrics.actualBoundingBoxRight ||
          metrics.width
      );

      const ascent = Math.ceil(
        metrics.actualBoundingBoxAscent ||
          resolvedSize * 0.78
      );

      const descent = Math.ceil(
        metrics.actualBoundingBoxDescent ||
          resolvedSize * 0.22
      );

      const padding = Math.max(
        12,
        Math.ceil(resolvedSize * 0.08)
      );

      const textWidth = Math.max(
        1,
        left + right
      );

      const textHeight = Math.max(
        1,
        ascent + descent
      );

      offscreen.width =
        textWidth + padding * 2;

      offscreen.height =
        textHeight + padding * 2;

      offCtx.clearRect(
        0,
        0,
        offscreen.width,
        offscreen.height
      );

      offCtx.font = font;
      offCtx.textAlign = "left";
      offCtx.textBaseline = "alphabetic";
      offCtx.fillStyle = "#ffffff";

      offCtx.fillText(
        content,
        padding - left,
        padding + ascent
      );

      const imageData =
        offCtx.getImageData(
          0,
          0,
          offscreen.width,
          offscreen.height
        );

      const targets = [];
      const step = Math.max(
        2,
        Math.floor(density)
      );

      for (
        let y = 0;
        y < offscreen.height;
        y += step
      ) {
        for (
          let x = 0;
          x < offscreen.width;
          x += step
        ) {
          const alpha =
            imageData.data[
              (y * offscreen.width + x) *
                4 +
                3
            ];

          if (alpha > 40) {
            targets.push({
              x:
                width / 2 -
                offscreen.width / 2 +
                x,
              y:
                height / 2 -
                offscreen.height / 2 +
                y,
              alpha: alpha / 255,
            });
          }
        }
      }

      const maxParticles = Math.max(
        900,
        Math.min(
          5200,
          Math.floor(
            (width * height) / 90
          )
        )
      );

      const stride = Math.max(
        1,
        Math.ceil(
          targets.length / maxParticles
        )
      );

      const baseRgb = hexToRgb(color);
      const highlightRgb =
        hexToRgb(highlightColor);

      const selectedTargets =
        targets.filter(
          (_, index) =>
            index % stride === 0
        );

      particles = selectedTargets.map(
        (target, index) => {
          const seed =
            ((index * 9301 + 49297) %
              233280) /
            233280;

          const depth =
            0.45 +
            (((index * 233 + 97) %
              1000) /
              1000) *
              0.9;

          const blend =
            baseRgb && highlightRgb
              ? clamp(
                  target.x /
                    Math.max(1, width) +
                    (seed - 0.5) * 0.35,
                  0,
                  1
                )
              : 0;

          const particleColor =
            baseRgb && highlightRgb
              ? rgbToCss(
                  mixRgb(
                    baseRgb,
                    highlightRgb,
                    blend
                  )
                )
              : color;

          const angle =
            seed * Math.PI * 2;

          const distance =
            (reducedMotion ? 0 : scatter) *
            (0.35 + depth * 0.75);

          const startX =
            target.x +
            Math.cos(angle) * distance +
            (seed - 0.5) *
              scatter *
              0.45;

          const startY =
            target.y +
            Math.sin(angle) * distance +
            (depth - 0.9) *
              scatter *
              0.45;

          return {
            x: reducedMotion
              ? target.x
              : startX,
            y: reducedMotion
              ? target.y
              : startY,
            startX,
            startY,
            targetX: target.x,
            targetY: target.y,
            size: Math.max(
              0.6,
              particleSize *
                (0.75 +
                  target.alpha * 0.45)
            ),
            color: particleColor,
            seed,
            depth,
            delay: seed * stagger,
          };
        }
      );

      pointer.x = width / 2;
      pointer.y = height / 2;
      pointer.smoothX = pointer.x;
      pointer.smoothY = pointer.y;

      if (reducedMotion) {
        particles.forEach(
          (particle) => {
            particle.x =
              particle.targetX;

            particle.y =
              particle.targetY;

            particle.startX =
              particle.targetX;

            particle.startY =
              particle.targetY;

            particle.delay = 0;
          }
        );

        gathering = false;
      } else {
        startGather(false);
      }

      ensureRenderLoop();
    };

    const queueSample = () => {
      if (resizeFrame) {
        window.cancelAnimationFrame(
          resizeFrame
        );
      }

      resizeFrame =
        window.requestAnimationFrame(
          sampleText
        );
    };

    const handlePointerMove = (event) => {
      const rect =
        canvas.getBoundingClientRect();

      pointer.x =
        event.clientX - rect.left;

      pointer.y =
        event.clientY - rect.top;

      pointer.active = true;
    };

    const handlePointerLeave = () => {
      pointer.active = false;
    };

    const handlePointerEnter = (event) => {
      handlePointerMove(event);

      if (trigger === "hover") {
        startGather(true);
      }
    };

    const handleClick = () => {
      if (trigger === "click") {
        startGather(true);
      }
    };

    const reduceMotionQuery =
      window.matchMedia?.(
        "(prefers-reduced-motion: reduce)"
      );

    const handleReduceMotionChange = (
      event
    ) => {
      reducedMotion = event.matches;
      sampleText();
    };

    reduceMotionQuery?.addEventListener(
      "change",
      handleReduceMotionChange
    );

    canvas.addEventListener(
      "pointerenter",
      handlePointerEnter
    );

    canvas.addEventListener(
      "pointermove",
      handlePointerMove
    );

    canvas.addEventListener(
      "pointerleave",
      handlePointerLeave
    );

    canvas.addEventListener(
      "click",
      handleClick
    );

    const resizeObserver =
      new ResizeObserver(queueSample);

    resizeObserver.observe(container);

    sampleText();

    return () => {
      buildId += 1;

      resizeObserver.disconnect();

      reduceMotionQuery?.removeEventListener(
        "change",
        handleReduceMotionChange
      );

      canvas.removeEventListener(
        "pointerenter",
        handlePointerEnter
      );

      canvas.removeEventListener(
        "pointermove",
        handlePointerMove
      );

      canvas.removeEventListener(
        "pointerleave",
        handlePointerLeave
      );

      canvas.removeEventListener(
        "click",
        handleClick
      );

      if (animationFrame !== null) {
        window.cancelAnimationFrame(
          animationFrame
        );
      }

      if (resizeFrame !== null) {
        window.cancelAnimationFrame(
          resizeFrame
        );
      }
    };
  }, [
    text,
    particleSize,
    density,
    color,
    highlightColor,
    scatter,
    gatherDuration,
    stagger,
    pointerRepel,
    repelRadius,
    idleDrift,
    trigger,
    fontSize,
    fontWeight,
    fontFamily,
    glow,
  ]);

  return (
    <div
      ref={containerRef}
      className={`
        relative
        block
        h-full
        min-h-[240px]
        w-full
        overflow-hidden
        touch-none
        isolate
        ${className}
      `}
      style={style}
      aria-label={text}
    >
      <canvas
        ref={canvasRef}
        className="
          absolute
          inset-0
          block
          h-full
          w-full
        "
        aria-hidden="true"
      />

      <span className="sr-only">
        {text}
      </span>
    </div>
  );
}


/* =========================================================
   CONTACT PATH
========================================================= */

function ContactPath({ active, icon, title, subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        group
        relative
        overflow-hidden
        rounded-[16px]
        border
        p-4
        text-left
        transition-all
        duration-300

        ${
          active
            ? `
              border-[#55aef3]
              bg-blue-50
            `
            : `
              border-neutral-200
              bg-white
              hover:border-neutral-400
            `
        }
      `}
    >
      <div
        className="
          flex
          items-center
          justify-between
        "
      >
        <div
          className={`
            flex
            h-10
            w-10
            items-center
            justify-center
            rounded-xl
            transition

            ${
              active
                ? `
                  bg-[#55aef3]
                  text-white
                `
                : `
                  bg-neutral-100
                  text-neutral-500
                `
            }
          `}
        >
          {icon}
        </div>

        {active && (
          <motion.div
            initial={{
              scale: 0,
            }}
            animate={{
              scale: 1,
            }}
            className="
              flex
              h-5
              w-5
              items-center
              justify-center
              rounded-full
              bg-[#55aef3]
              text-white
            "
          >
            <IconCheck size={12} />
          </motion.div>
        )}
      </div>

      <p
        className="
          mt-5
          text-sm
          font-black
        "
      >
        {title}
      </p>

      <p
        className="
          mt-1
          text-[11px]
          text-neutral-400
        "
      >
        {subtitle}
      </p>
    </button>
  );
}

/* =========================================================
   CUSTOM INTEREST DROPDOWN
========================================================= */

function InterestDropdown({ contactType, onSelect }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("");

  const instituteOptions = [
    "MERN Stack",
    "Digital Marketing Course",
    "UI/UX",
    "Python Full Stack",
    "Cyber Security",
    "Tally Prime",
    "Basic Computers",
  ];

  const technologyOptions = [
    "Web Application",
    "Website Development",
    "Landing Page",
    "Mobile App",
    "Billing / POS Software",
    "Animated Website",
    "SEO",
    "Google Ads",
    "Digital Marketing",
    "Video Editing",
    "Other",
  ];

  const options =
    contactType === "institute" ? instituteOptions : technologyOptions;

  return (
    <div className="relative">
      <label
        className="
          text-[10px]
          font-bold
          uppercase
          tracking-[0.15em]
          text-neutral-400
        "
      >
        I'm interested in
      </label>

      <input type="hidden" name="interest" value={selected} />

      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={`
          group
          mt-3
          flex
          w-full
          items-center
          justify-between
          gap-4
          rounded-[14px]
          border
          px-4
          py-[14px]
          text-left
          text-sm
          transition-all
          duration-300

          ${
            open
              ? `
                  border-[#55aef3]
                  bg-blue-50/50
                  shadow-[0_0_0_3px_rgba(85,174,243,0.08)]
                `
              : `
                  border-neutral-200
                  bg-[#f8f8f8]
                  hover:border-neutral-300
                  hover:bg-white
                `
          }
        `}
      >
        <span
          className={
            selected
              ? "font-semibold text-neutral-900"
              : "text-neutral-400"
          }
        >
          {selected || "Select an option"}
        </span>

        <motion.span
          animate={{
            rotate: open ? 180 : 0,
          }}
          transition={{
            duration: 0.2,
          }}
          className={`
            flex
            h-7
            w-7
            shrink-0
            items-center
            justify-center
            rounded-lg
            transition-colors
            duration-300

            ${
              open
                ? "bg-[#55aef3] text-white"
                : "bg-white text-neutral-400 shadow-sm"
            }
          `}
        >
          <IconChevronDown size={15} stroke={2} />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{
              opacity: 0,
              y: -8,
              scale: 0.98,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: -8,
              scale: 0.98,
            }}
            transition={{
              duration: 0.18,
            }}
            role="listbox"
            className="
              absolute
              left-0
              right-0
              top-[calc(100%+8px)]
              z-[100]
              max-h-[270px]
              overflow-y-auto
              rounded-[16px]
              border
              border-neutral-200
              bg-white
              p-2
              shadow-[0_20px_60px_rgba(0,0,0,0.12)]
            "
          >
            {options.map((option, index) => {
              const isSelected = selected === option;

              return (
                <button
                  key={option}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    setSelected(option);
                    setOpen(false);
                    onSelect?.(option);
                  }}
                  className={`
                    group/item
                    flex
                    w-full
                    items-center
                    justify-between
                    gap-3
                    rounded-[11px]
                    px-3
                    py-3
                    text-left
                    text-sm
                    transition-all
                    duration-200

                    ${
                      isSelected
                        ? "bg-blue-50 font-bold text-[#3899e4]"
                        : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-950"
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="
                        w-5
                        font-mono
                        text-[9px]
                        text-neutral-300
                      "
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <span>{option}</span>
                  </div>

                  {isSelected && (
                    <span
                      className="
                        flex
                        h-5
                        w-5
                        items-center
                        justify-center
                        rounded-full
                        bg-[#55aef3]
                        text-white
                      "
                    >
                      <IconCheck size={11} stroke={2.5} />
                    </span>
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* =========================================================
   CONTACT FIELD
========================================================= */

function ContactField({ label, ...props }) {
  return (
    <div>
      <label
        className="
          text-[10px]
          font-bold
          uppercase
          tracking-[0.15em]
          text-neutral-400
        "
      >
        {label}
      </label>

      <input
        {...props}
        className="
          mt-3
          w-full
          border-0
          border-b
          border-neutral-200
          bg-transparent
          px-0
          py-3
          text-sm
          outline-none
          transition
          placeholder:text-neutral-300
          focus:border-[#55aef3]
        "
      />
    </div>
  );
}

/* =========================================================
   SUCCESS ANIMATION
========================================================= */

function SuccessMessage({ onReset }) {
  const particles = Array.from({
    length: 12,
  });

  return (
    <motion.div
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      className="
        relative
        flex
        min-h-[600px]
        items-center
        justify-center
        overflow-hidden
      "
    >
      {/* SUCCESS PARTICLES */}

      {particles.map((_, index) => {
        const angle = (index / particles.length) * Math.PI * 2;

        const x = Math.cos(angle) * 150;

        const y = Math.sin(angle) * 150;

        return (
          <motion.span
            key={index}
            initial={{
              x: 0,
              y: 0,
              opacity: 1,
              scale: 0,
            }}
            animate={{
              x,
              y,
              opacity: 0,
              scale: [0, 1, 0.5],
            }}
            transition={{
              duration: 1.2,

              delay: 0.15,

              ease: "easeOut",
            }}
            className="
                absolute
                left-1/2
                top-1/2
                h-2
                w-2
                rounded-full
                bg-[#55aef3]
              "
          />
        );
      })}

      <div
        className="
          relative
          z-10
          max-w-md
          text-center
        "
      >
        {/* THUMBS UP */}

        <motion.div
          initial={{
            scale: 0,
            rotate: -25,
          }}
          animate={{
            scale: [0, 1.2, 0.95, 1],

            rotate: [-25, 8, -3, 0],
          }}
          transition={{
            duration: 0.9,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="
            relative
            mx-auto
            flex
            h-28
            w-28
            items-center
            justify-center
            rounded-full
            bg-[#55aef3]
            text-white
            shadow-2xl
            shadow-blue-200
          "
        >
          <IconThumbUp size={48} stroke={1.8} />

          <motion.div
            animate={{
              rotate: 360,
            }}
            transition={{
              duration: 8,

              repeat: Infinity,

              ease: "linear",
            }}
            className="
              absolute
              -inset-5
              rounded-full
              border
              border-dashed
              border-[#55aef3]/40
            "
          />
        </motion.div>

        {/* TEXT */}

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            delay: 0.5,
          }}
        >
          <p
            className="
              mt-8
              text-[10px]
              font-bold
              uppercase
              tracking-[0.2em]
              text-[#3899e4]
            "
          >
            Message received
          </p>

          <h3
            className="
              merriweather
              mt-3
              text-3xl
              font-black
              tracking-[-0.04em]
            "
          >
            Nice.
            <span
              className="
                block
                text-[#55aef3]
              "
            >
              We'll take it from here.
            </span>
          </h3>

          <p
            className="
              mx-auto
              mt-5
              max-w-sm
              text-sm
              leading-7
              text-neutral-500
            "
          >
            Your enquiry has been received. Our team can review your requirement
            and continue the conversation with you.
          </p>

          <button
            onClick={onReset}
            className="
              mt-7
              text-xs
              font-bold
              text-neutral-400
              underline
              underline-offset-4
              transition
              hover:text-black
            "
          >
            Send another enquiry
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
}

/* =========================================================
   JOURNEY
========================================================= */

function JourneyRow({
  number,
  icon,
  title,
  name,
  description,
  link,
  linkText,
  last = false,
}) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 30,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      viewport={{
        once: true,
      }}
      className={`
        group
        grid
        gap-6
        py-9
        transition
        sm:grid-cols-[60px_70px_1fr_auto]
        sm:items-center

        ${!last ? "border-b border-neutral-200" : ""}
      `}
    >
      <span
        className="
          font-mono
          text-xs
          text-neutral-300
        "
      >
        {number}
      </span>

      <div
        className="
          flex
          h-12
          w-12
          items-center
          justify-center
          rounded-xl
          bg-white
          text-[#55aef3]
          transition
          group-hover:bg-[#55aef3]
          group-hover:text-white
        "
      >
        {icon}
      </div>

      <div>
        <p
          className="
            text-xs
            font-bold
            uppercase
            tracking-[0.13em]
            text-[#3899e4]
          "
        >
          {name}
        </p>

        <h3
          className="
            merriweather
            mt-2
            text-2xl
            font-black
          "
        >
          {title}
        </h3>

        <p
          className="
            mt-2
            max-w-xl
            text-sm
            leading-7
            text-neutral-500
          "
        >
          {description}
        </p>
      </div>

      <Link
        to={link}
        className="
          group/link
          inline-flex
          items-center
          gap-2
          text-xs
          font-bold
        "
      >
        {linkText}

        <IconArrowUpRight
          size={15}
          className="
            transition-transform
            group-hover/link:translate-x-1
            group-hover/link:-translate-y-1
          "
        />
      </Link>
    </motion.div>
  );
}

/* =========================================================
   LOCATION TAG
========================================================= */

function LocationTag({ text }) {
  return (
    <span
      className="
        rounded-full
        border
        border-neutral-200
        bg-[#f8f8f8]
        px-4
        py-2
        text-[11px]
        font-semibold
        text-neutral-600
      "
    >
      {text}
    </span>
  );
}

/* =========================================================
   FAQ ITEM
========================================================= */

function FaqItem({ faq, index, open, onClick }) {
  return (
    <div
      className="
        border-b
        border-white/10
      "
    >
      <button
        onClick={onClick}
        className="
          grid
          w-full
          grid-cols-[42px_1fr_auto]
          items-start
          gap-3
          py-7
          text-left
        "
      >
        <span
          className="
            pt-1
            font-mono
            text-[10px]
            text-[#55aef3]
          "
        >
          {String(index + 1).padStart(2, "0")}
        </span>

        <span
          className="
            merriweather
            text-base
            font-bold
            leading-7
            text-white/90
            sm:text-lg
          "
        >
          {faq.question}
        </span>

        <motion.div
          animate={{
            rotate: open ? 180 : 0,
          }}
          className="
            mt-1
            text-white/30
          "
        >
          <IconChevronDown size={18} />
        </motion.div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{
              height: 0,
              opacity: 0,
            }}
            animate={{
              height: "auto",
              opacity: 1,
            }}
            exit={{
              height: 0,
              opacity: 0,
            }}
            transition={{
              duration: 0.3,
            }}
            className="
              overflow-hidden
            "
          >
            <p
              className="
                max-w-2xl
                pb-7
                pl-[55px]
                pr-8
                text-sm
                leading-7
                text-white/45
              "
            >
              {faq.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
